import { Button, CircularProgress, Stack, Typography } from "@compass/core";
import { Student } from "generated/openApi";
import { useDispatch } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import { newToast } from "@compass/core";
import ApiService from "services/apiService";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useEffect } from "react";

interface StudentQrPageBodyProps {
    student: Student;
    setStudent: (updater: (draft: Student) => void) => void;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
}

export const StudentQrPageBody = (props: StudentQrPageBodyProps) => {
    const dispatch = useDispatch();

    //Check if QR code exists
    const hasQrCode = !!(props.student.qrCodeUploadFile?.fileRef && props.student.qrCodeUploadFile?.fileName);

    const isOldUrlFormat = (): boolean => {
        return hasQrCode && !props.student.qrCodeUploadFile?.imagePreviewData;
    };

    //Generate QR code if it doesn't exist
    const generateQrCode = async () => {
        if (!props.student.id) return;

        props.setIsLoading(true);
        try {
            const response = await ApiService.students.generateQrCode(props.student.id);

            //Update student with new QR code
            props.setStudent((draft) => {
                draft.qrCodeUploadFile = response;
            });

            const isRegeneration = hasQrCode;
            const message = isRegeneration
                ? "QR Code updated successfully"
                : "QR Code generated successfully";
            dispatch(setAppToast(newToast("success", message)));
        } catch (error) {
            dispatch(setAppToast(newToast("error", "Failed to generate QR Code")));
        } finally {
            props.setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOldUrlFormat() && !props.isLoading) {
            console.log('Detected old QR code format, auto-regenerating...');
            generateQrCode();
        }
    }, [props.student.qrCodeUploadFile, props.isLoading]);

    const handleDownload = () => {
        if (!props.student.qrCodeUploadFile?.imagePreviewData) return;
        const { imagePreviewData } = props.student.qrCodeUploadFile;
        const studentName = props.student.fullName;
        const studentId = props.student.customId1;
        const fileName = `QR_${studentName}_${studentId}.png`;
        const link = document.createElement("a");
        link.href = imagePreviewData;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Stack spacing="lg" alignItems="center">
            {hasQrCode ? (
                <>
                    {props.student.customId1 && (
                        <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ fontStyle: 'italic' }}>
                            District ID: {props.student.customId1}
                        </Typography>
                    )}
                    {props.student.qrCodeUploadFile?.imagePreviewData && (
                        <img
                            src={props.student.qrCodeUploadFile.imagePreviewData}
                            alt="Student QR Code"
                            style={{
                                maxWidth: '200px',
                                maxHeight: '200px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '8px',
                                backgroundColor: '#fff'
                            }}
                        />
                    )}

                    <Button
                        variant="contained"
                        onClick={handleDownload}
                        startIcon={<FileDownloadIcon />}
                        sx={{ textTransform: "none", fontWeight: "bold" }}
                    >
                        Download QR
                    </Button>
                </>
            ) : (
                //Show generate button if no QR code exists
                <>
                    <Typography variant="body1" color="textSecondary" textAlign="center">
                        No QR Code exists for {props.student.fullName || "this student"}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={generateQrCode}
                        disabled={props.isLoading}
                    >
                        {props.isLoading ? (
                            <>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Generating...
                            </>
                        ) : (
                            "Generate QR Code"
                        )}
                    </Button>
                </>
            )}
        </Stack>
    );
};